/**
 * The write behaviour every opener control shares (PC-I54, PC-I61): the
 * controls wait while a request is in flight, a write that was not saved
 * leaves them where they were with a note under them, and the keyboard is
 * not lost when the control it was on is replaced by the answer's own.
 *
 * `ProposalControls` (the verdict's, #270) carries the same logic inline. It
 * is left as it is: no test pins its container's behaviour, and PC-I75 says
 * not to refactor it without one. This hook is what the controls after it
 * share.
 */
import { useEffect, useRef, useState } from "react";

/**
 * @param view What the controls currently show. When it changes and the
 * keyboard was on one of the controls as a request went out, focus moves to
 * the first button of the new view: the control it was on is gone.
 */
export function useOpenerRun(view: unknown) {
  const [busy, setBusy] = useState(false);
  // The last answer or dismissal was not saved (PC-I54).
  const [failed, setFailed] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  // Whether the keyboard was on one of the controls when a write went out.
  const hadFocus = useRef(false);

  useEffect(() => {
    const root = box.current;
    if (busy || !hadFocus.current || !root) return;
    hadFocus.current = false;
    if (!root.contains(document.activeElement)) {
      root.querySelector<HTMLButtonElement>("button")?.focus();
    }
  }, [view, busy]);

  // Every control waits while a request is in flight; a failed one leaves
  // them where they were, with no record line claiming what did not happen,
  // and a note under them that says so (PC-I54). Trying again clears it.
  const run = async (act: () => Promise<unknown> | void) => {
    hadFocus.current = box.current?.contains(document.activeElement) ?? false;
    setFailed(false);
    setBusy(true);
    try {
      if ((await act()) === false) setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return { box, busy, failed, run };
}
